'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { usePathname } from 'next/navigation';

import { GroqImage } from '@/helpers/Image';
import { useCarouselApi } from '@/hooks/useCarouselApi';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DEFAULT_LANGUAGE, NON_DEFAULT_LANGUAGES, resolveI18nText } from '@/sanity/globals/I18n';
import { ITeamMember } from '@/sanity/lib/definitions';
import { PageCareerOverviewSchema } from '@/sanity/pages/PageCareerOverviewTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';

const ease = [0.25, 0.1, 0.25, 1] as const;

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 12.75 10.625" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MemberCard({ member, language }: { member: ITeamMember; language: string }) {
  const role = resolveI18nText(member.role, member.roleI18n, language);

  return (
    <div className="flex w-full flex-col">
      <div className="relative aspect-[289/384] w-full overflow-hidden rounded-[0.25rem] bg-black">
        {member.image && (
          <GroqImage
            src={member.image}
            alt={member.name || ''}
            quality={100}
            className="!absolute !inset-0 !h-full !w-full !object-cover"
          />
        )}
      </div>
      {member.name && (
        <p className="mb-0 mt-[2.5rem] text-[2.25rem] font-semibold leading-[1.5] text-black">
          {member.name}
        </p>
      )}
      {role && (
        <p className="mb-0 mt-[0.75rem] font-geist text-[1.9rem] uppercase leading-[2.25rem] text-black/70 lg:font-geist-mono lg:text-[1.75rem] lg:text-black/[0.45]">
          {role}
        </p>
      )}
    </div>
  );
}

export default function CareerTeamSection({ data }: { data: PageCareerOverviewSchema }) {
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const { setApi, handlePrev, handleNext, current, count, isFirst, isLast } = useCarouselApi();
  const language =
    NON_DEFAULT_LANGUAGES.find((l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`) ??
    DEFAULT_LANGUAGE;
  const members = data.teamMembers ?? [];
  if (!data.teamTitle && members.length === 0) return null;

  const progress = count > 0 ? current / count : 0;

  return (
    <section className="relative w-full overflow-hidden bg-[#f4f5f5] px-[2rem] pb-[8rem] pt-[7.5rem] lg:px-[6rem] lg:py-[15rem]">
      <div
        className="pointer-events-none absolute right-0 top-0 z-0 h-[24.7rem] w-[28.7rem] lg:hidden"
        style={{
          backgroundImage: 'url(/careers/hero-dots.png)',
          backgroundSize: '106rem auto',
          backgroundRepeat: 'repeat',
          opacity: 0.1,
        }}
        aria-hidden="true"
      />

      <div className="relative z-[1] mx-auto flex w-[min(169.5rem,100%)] flex-col items-center">
        {data.teamChip && (
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease }}
            className="mb-0 text-center font-blacker text-[2rem] italic leading-[1.5] text-black"
          >
            {data.teamChip}
          </motion.p>
        )}

        {data.teamTitle && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="richtext mt-[1.75rem] w-full prose-headings:mb-0 prose-headings:text-[4.25rem] prose-headings:leading-[1.15] prose-headings:tracking-[-0.07em] max-lg:[&_span[style*=linear-gradient]]:!bg-[linear-gradient(to_top,#C6DED9_55%,transparent_55%)] max-lg:[&_span[style*=linear-gradient]]:px-[0.35em] lg:mt-[2.5rem] lg:prose-headings:text-[6rem] lg:prose-headings:leading-[1.1] lg:prose-headings:tracking-[-0.055em]"
          >
            <PortableText value={data.teamTitle} components={richTextComponent} />
          </motion.div>
        )}

        {members.length > 0 && (
          <Carousel
            setApi={setApi}
            opts={{ align: 'start', breakpoints: { '(min-width: 993px)': { active: false } } }}
            className="max-lg:mt-[3.4rem] max-lg:w-[calc(100%_+_4rem)] lg:mt-[4rem] lg:w-[min(150.25rem,100%)]"
          >
            <CarouselContent className="lg:!overflow-visible lg:flex-wrap lg:gap-x-[2rem] lg:gap-y-[4rem]">
              {members.map((member, index) => (
                <CarouselItem
                  key={member._id ?? index}
                  className="max-lg:basis-[38.0625rem] max-lg:last:basis-[40.0625rem] max-lg:last:pr-[2rem] lg:basis-[calc((100%_-_6rem)_/_4)] lg:!pl-0"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    {...(isMobile
                      ? { animate: { opacity: 1, y: 0 } }
                      : { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.25 } })}
                    transition={{ duration: 0.7, ease, delay: Math.min(index * 0.06, 0.35) }}
                  >
                    <MemberCard member={member} language={language} />
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        )}

        {members.length > 1 && (
          <div className="mt-[4.25rem] flex w-full items-center gap-[1.9rem] lg:hidden">
            <button
              type="button"
              onClick={handlePrev}
              disabled={isFirst}
              aria-label="Previous"
              className="flex h-[4.0625rem] w-[4.0625rem] shrink-0 items-center justify-center rounded-full bg-black/10 transition-opacity disabled:opacity-30"
            >
              <ArrowIcon className="h-[1.275rem] w-[1.525rem] rotate-180 text-black" />
            </button>

            <div className="h-[0.475rem] flex-1 overflow-hidden rounded-full bg-black/[0.15]">
              <motion.div
                className="h-full rounded-full bg-black"
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.3, ease }}
              />
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={isLast}
              aria-label="Next"
              className="flex h-[4.0625rem] w-[4.0625rem] shrink-0 items-center justify-center rounded-full bg-black transition-opacity disabled:opacity-30"
            >
              <ArrowIcon className="h-[1.275rem] w-[1.525rem] text-white" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
